import { z } from "zod";
import type { Id } from "@convex/_generated/dataModel";
import type { Infer } from "convex/values";
import type { equipmentTypeValidator } from "@convex/schemas/shipments";

import { validators } from "./core/primitives";
import { contactSchema } from "./core/contact";
import { addressSchema } from "./core/address";
const rfqContactFields = {
  firstName: validators.text.required("First name is required"),
  lastName: validators.text.required("Last name is required"),
  email: validators.email.required(),
  phone: validators.phone.required(),
  company: validators.text.optional(),
};

const rfqLocationFields = {
  line1: validators.text.required("Street address is required"),
  city: validators.text.required("City is required"),
  state: validators.state.required(),
  postalCode: validators.postalCode.required(),
};
import {
  ACCOUNT_TYPES,
  BUSINESS_STRUCTURES,
  CONTACT_TYPES,
  PAYMENT_TERMS_OPTIONS,
  type AccountType,
  type BusinessStructure,
  type ContactType,
  type PaymentTerms,
} from "@convex/constants/types";
import { GLOBAL_ROLES, type GlobalRole } from "@shared/auth/roles";

export const accountTypes = ACCOUNT_TYPES;
export type { AccountType };

export const companySchema = z.object({
  name: validators.text.required("Company name is required"),
  email: validators.email.required(),
  phone: validators.phone.optional(),
  website: validators.url.optional(),
  accountType: z.enum(accountTypes).optional(),
});

export type Company = z.infer<typeof companySchema>;

export const userSchema = z.object({
  firstName: validators.text.required("First name is required"),
  lastName: validators.text.required("Last name is required"),
  email: validators.email.required(),
  phone: validators.phone.optional(),
  title: validators.text.optional(),
});

export type User = z.infer<typeof userSchema>;

export const checkoutSchema = z.object({
  ...rfqContactFields,
  ...rfqLocationFields,
  deliveryDate: z.string().optional(),
});

export type CheckoutFormValues = z.infer<typeof checkoutSchema>;

export const packagingMaterials = [
  "Pallets",
  "Cardboard Bales",
  "Gaylord Boxes",
  "IBC Totes",
  "Other Industrial Packaging",
] as const;
export type PackagingMaterial = (typeof packagingMaterials)[number];

export const serviceOptions = ["Buy", "Sell", "Recycle", "Other"] as const;
export type ServiceOption = (typeof serviceOptions)[number];

export const wasteAuditFormSchema = z.object({
  company: validators.text.required("Company name is required"),
  firstName: validators.text.required("First name is required"),
  lastName: validators.text.optional(),
  email: validators.email.required(),
  phone: validators.phone.required(),
  packagingMaterials: z.array(z.enum(packagingMaterials)).optional(),
  estimatedVolume: validators.text.required(
    "Estimated yearly volume is required",
  ),
  service: z.enum(serviceOptions, { message: "Please select a service" }),
  additionalInfo: validators.text.optional(),
  botcheck: z.boolean().optional(),
});

export type WasteAuditFormValues = z.infer<typeof wasteAuditFormSchema>;

export const inquiryTypes = [
  "order_issue",
  "account",
  "sales",
  "feedback",
  "support",
] as const;
export type InquiryType = (typeof inquiryTypes)[number];

export const INQUIRY_TYPES = [
  { value: "order_issue", label: "An order" },
  { value: "account", label: "My account" },
  { value: "sales", label: "Pricing" },
  { value: "feedback", label: "Feedback" },
  { value: "support", label: "Something else" },
] as const satisfies ReadonlyArray<{ value: InquiryType; label: string }>;

export const contactPageFormSchema = z
  .object({
    name: validators.text.required("Name is required"),
    email: validators.email.required(),
    phone: validators.phone.optional(),
    company: validators.text.optional(),
    orderReference: validators.text.optional(),
    inquiryType: z.enum(inquiryTypes),
    message: validators.message.required("Please provide more details"),
    consent: validators.consent,
    smsConsent: z.boolean().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.smsConsent && !value.phone) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["phone"],
        message: "Phone is required for texts",
      });
    }
  });

export type ContactPageFormValues = z.infer<typeof contactPageFormSchema>;

export const globalRoles = GLOBAL_ROLES;
export type { GlobalRole };

export const adminUserFormSchema = z.object({
  firstName: validators.text.optional(),
  lastName: validators.text.optional(),
  email: validators.email.required(),
  phone: validators.phone.optional(),
  title: validators.text.optional(),
  role: z.enum(globalRoles),
  companyId: validators.text.optional(),
});

export type AdminUserFormValues = z.infer<typeof adminUserFormSchema>;

export const contactTypes = CONTACT_TYPES;
export type { ContactType };

export const adminContactFormSchema = contactSchema.extend({
  contactType: z.enum(contactTypes),
  billingAddress: addressSchema,
  shippingAddress: addressSchema,
  sameAsShipping: z.boolean(),
  sendInvitation: z.boolean(),
  createStripeCustomer: z.boolean(),
});

export type AdminContactFormValues = z.infer<typeof adminContactFormSchema>;

export const crmContactDialogSchema = z.object({
  firstName: validators.text.required("First name is required"),
  lastName: validators.text.required("Last name is required"),
  email: validators.email.required(),
  phone: validators.phone.optional(),
  title: validators.text.optional(),
  company: validators.text.optional(),
  contactType: z.enum(contactTypes),
});

export type CrmContactDialogValues = z.infer<typeof crmContactDialogSchema>;

export const businessStructures = BUSINESS_STRUCTURES;
export type { BusinessStructure };

export const paymentTerms = PAYMENT_TERMS_OPTIONS;
export type { PaymentTerms };

const einSchema = validators.ein.optional();

const w9DocumentSchema = z
  .object({
    storageId: z.custom<Id<"_storage">>((val) => typeof val === "string"),
    filename: z.string(),
    mimeType: z.string(),
    fileSize: z.number(),
  })
  .optional();

export const adminCompanyFormSchema = companySchema
  .omit({ accountType: true, email: true, phone: true })
  .extend({
    accountType: z.enum(accountTypes),
    companyEmail: validators.email.optional(),
    companyPhone: validators.phone.optional(),
    legalBusinessName: validators.text.optional(),
    ein: einSchema,
    businessStructure: z.enum([...businessStructures, "" as const]).optional(),
    w9Document: w9DocumentSchema,
    sendOnboarding: z.boolean(),
  });

export type AdminCompanyFormValues = z.infer<typeof adminCompanyFormSchema>;

export const equipmentTypes = ["DRY", "REFRIGERATED", "FLATBED"] as const;
export type EquipmentType = Infer<typeof equipmentTypeValidator>;

export const freightCalculatorSchema = z.object({
  origin: validators.postalCode.required(),
  destination: validators.postalCode.required(),
  quantity: validators.number.int(),
  weight_lbs: z.number().int().positive("Weight must be positive"),
  equipment_type: z.enum(equipmentTypes),
});

export type FreightCalculatorValues = z.infer<typeof freightCalculatorSchema>;

export const shippingTypes = [
  "DRYVAN",
  "REEFER",
  "FLATBED",
  "VAN",
  "TEMPERATURE CONTROL",
] as const;
export type ShippingType = (typeof shippingTypes)[number];

export const simpleCalculatorSchema = z.object({
  origin: validators.postalCode.required(),
  destination: validators.postalCode.required(),
  pickup: validators.text.required("Pickup date is required"),
  type: z.enum(shippingTypes),
});

export type SimpleCalculatorValues = z.infer<typeof simpleCalculatorSchema>;

export const fields = {
  firstName: validators.text.required("First name is required"),
  lastName: validators.text.required("Last name is required"),
  lastNameOptional: validators.text.optional(),
  email: validators.email.required(),
  emailOptional: validators.email.optional(),
  phone: validators.phone.required(),
  phoneOptional: validators.phone.optional(),
  company: validators.text.optional(),
  companyRequired: validators.text.required("Company name is required"),
  title: validators.text.optional(),
  consent: validators.consent,
  message: validators.message.required(),
  messageOptional: validators.message.optional(),
  notes: validators.message.optional(),
};

export const FIELD_NAMES = {
  firstName: "firstName",
  lastName: "lastName",
  email: "email",
  phone: "phone",
  company: "company",
  title: "title",
  line1: "line1",
  line2: "line2",
  city: "city",
  state: "state",
  postalCode: "postalCode",
  country: "country",
  message: "message",
  notes: "notes",
  consent: "consent",
} as const;

export type FieldName = keyof typeof FIELD_NAMES;

export const emailSchema = validators.email.required();
export const emailOptionalSchema = validators.email.optional();
export const phoneSchema = validators.phone.required();
export const phoneOptionalSchema = validators.phone.optional();
export const zipCodeSchema = validators.postalCode.required();
export const zipCodeOptionalSchema = validators.postalCode.optional();
export const nameSchema = validators.text.required("Name is required");
export const nameOptionalSchema = validators.text.optional();
export const companyNameSchema = validators.text.required(
  "Company name is required",
);
export const companyNameOptionalSchema = validators.text.optional();
export const quantitySchema = validators.number.int();
export const quantityOptionalSchema = validators.number.optional();
export const priceSchema = z.number().nonnegative("Price cannot be negative");
export const priceCentsSchema = z
  .number()
  .int()
  .nonnegative("Price cannot be negative");
export const messageSchema = validators.message.required();
export const messageOptionalSchema = validators.message.optional();
export const notesSchema = validators.message.optional();
export const urlSchema = validators.url.required();
export const urlOptionalSchema = validators.url.optional();
export const consentSchema = validators.consent;
export const titleSchema = validators.text.optional();
